'use client';

import { db } from '@util/firebase';
import {
  groupFitmentYears,
  removedOriginalFitments,
} from '@util/fitment/generationNotice';
import { logError } from '@util/logError';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  fitmentMetadataSchema,
  type FitmentMetadata,
} from 'models/fitmentMetadata';
import type { Bike } from 'models/shared';
import { useEffect, useState } from 'react';

function FitmentList({ title, bikes }: { title: string; bikes: Bike[] }) {
  return (
    <div>
      <h4 className="mb-2 font-semibold">{title}</h4>
      {bikes.length ? (
        <ul className="space-y-2">
          {groupFitmentYears(bikes).map((group) => (
            <li key={group.label}>
              <span className="font-medium">{group.label}:</span> {group.years}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-zinc-500">None</p>
      )}
    </div>
  );
}

export default function FitmentChanges({
  productId,
  bikes,
  enabled,
}: {
  productId: string;
  bikes: Bike[];
  enabled: boolean;
}) {
  const [state, setState] = useState<{
    productId: string;
    metadata?: FitmentMetadata;
    error?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !productId) return;
    return onSnapshot(
      doc(db, 'products', productId, 'fitment_metadata', 'generation'),
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({ productId });
          return;
        }
        const parsed = fitmentMetadataSchema.safeParse(snapshot.data());
        if (!parsed.success) {
          void logError(parsed.error, `fitment-metadata-${productId}`);
          setState({ productId, error: true });
          return;
        }
        setState({ productId, metadata: parsed.data });
      },
      (error) => {
        void logError(error, `fitment-metadata-${productId}`);
        setState({ productId, error: true });
      }
    );
  }, [enabled, productId]);

  if (!enabled || !productId) return null;
  const current = state?.productId === productId ? state : null;
  if (!current)
    return (
      <p className="text-[1.4rem] text-zinc-500">Loading fitment changes…</p>
    );
  if (current.error)
    return (
      <p className="text-[1.4rem] text-red-700">
        Unable to load fitment changes.
      </p>
    );
  if (!current.metadata) return null;
  const metadata = current.metadata;

  return (
    <details className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 text-[1.4rem]">
      <summary className="cursor-pointer font-semibold">
        Fitment changes · Support
      </summary>
      <div className="mt-5 space-y-5">
        {metadata.disabled && (
          <p className="font-medium text-amber-800">
            {metadata.review_required
              ? 'Enrichment paused: seller fitment needs review.'
              : 'Enrichment disabled.'}
          </p>
        )}
        <FitmentList
          title="Original listing fitment"
          bikes={metadata.original_bikes}
        />
        <FitmentList
          title="Current seller declarations"
          bikes={metadata.source_bikes}
        />
        <FitmentList title="Added by MX Locker" bikes={metadata.added_bikes} />
        <FitmentList
          title="Removed from original fitment"
          bikes={removedOriginalFitments(metadata.original_bikes, bikes)}
        />
        <p className="text-zinc-500">
          Last updated: {new Date(metadata.updated_at).toLocaleString()}
        </p>
      </div>
    </details>
  );
}
